import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Users, ExternalLink, UserCheck, Briefcase, RotateCcw, ArrowLeftRight, Info, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  open: { label: "Open Existing Person", Icon: ExternalLink, variant: "outline" },
  claim: { label: "Claim / Assign to Me", Icon: UserCheck, variant: "primary" },
  reassign: { label: "Reassign to Me", Icon: UserCheck, variant: "primary" },
  start_opportunity: { label: "Add Recruiting Lead / Opportunity to Existing Person", Icon: Briefcase, variant: "primary" },
  open_opportunity: { label: "Open Existing Recruiting Record", Icon: Briefcase, variant: "outline" },
  reactivate: { label: "Reactivate & Claim", Icon: RotateCcw, variant: "primary" },
  create_anyway: { label: "Create New Person", Icon: Info, variant: "ghost" },
  request_transfer: { label: "Request Transfer / Access", Icon: ArrowLeftRight, variant: "primary" },
};

export function DuplicatePersonResolver({ result, values, onReset, onClose, onCreated, setResult }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [reason, setReason] = useState("");
  const actions = result.allowed_actions || [];
  const crossOrg = result.scope_class === "cross_org";
  const pid = result.person_id;

  const go = (pathPid) => { onClose?.(); navigate(`/people/detail/${pathPid}`); };

  const run = async (action) => {
    setBusy(action);
    try {
      if (action === "open") { go(pid); return; }
      if (action === "open_opportunity") { go(pid); return; }
      if (action === "claim" || action === "reassign") {
        await api.post(`/person-match/${pid}/claim`, { assign_recruiter_id: "me", add_classification: "lead" });
        toast.success("Person assigned to you");
        onCreated?.({ person_id: pid });
        go(pid); return;
      }
      if (action === "reactivate") {
        await api.post(`/person-match/${pid}/claim`, { reactivate: true, assign_recruiter_id: "me", add_classification: "lead" });
        toast.success("Person reactivated and assigned to you");
        onCreated?.({ person_id: pid });
        go(pid); return;
      }
      if (action === "start_opportunity") {
        const { data } = await api.post(`/person-match/${pid}/start-opportunity`, { assign_to_me: !result.assigned_recruiter_id });
        toast[data.status === "created" ? "success" : "info"](data.message || "Opportunity started");
        onCreated?.({ person_id: pid });
        go(pid); return;
      }
      if (action === "create_anyway") {
        if (result.match_type === "phone" && !window.confirm("The phone number matches an existing Person. Create a separate new Person anyway? The existing record and login are not changed.")) return;
        const { data } = await api.post("/leads", { ...values, confirm_create: true });
        setResult?.(data);
        if (data.status === "created") { toast.success("Lead added"); onCreated?.(data); }
        return;
      }
      if (action === "request_transfer") {
        const { data } = await api.post("/person-match/transfer-requests", {
          first_name: values.first_name, last_name: values.last_name, email: values.email, mobile: values.mobile, reason,
        });
        toast.success(data.message || "Request submitted");
        onClose?.(); return;
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const btn = (action) => {
    const m = ACTION_META[action];
    if (!m) return null;
    const cls = m.variant === "primary" ? "bg-brand hover:bg-brand-strong text-white" : "";
    return (
      <Button key={action} variant={m.variant === "primary" ? "default" : m.variant} className={cls}
        disabled={!!busy || (action === "request_transfer" && !reason.trim())}
        onClick={() => run(action)} data-testid={`dup-action-${action}`}>
        <m.Icon className="w-4 h-4 mr-1.5" />{m.label}
      </Button>
    );
  };

  return (
    <div className="py-1" data-testid="duplicate-resolver">
      <div className={`flex items-start gap-2 rounded-lg border p-3 ${crossOrg ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
        {crossOrg ? <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" /> : <Users className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />}
        <div>
          <p className="font-display font-bold text-slate-900" data-testid="dup-title">{crossOrg ? "Match in another organization" : result.confidence === "possible" ? "Possible Existing Person" : "Existing Person Found"}</p>
          <p className="text-sm text-slate-600 mt-0.5" data-testid="dup-message">{result.message}{result.match_label && result.match_type !== "phone" ? ` Matched by ${result.match_label}.` : ""}{result.confidence === "possible" ? " If this is a different person, you can still create a new one." : " To prevent duplicate records, a new Person will not be created."}</p>
          {result.has_active_opportunity && <p className="text-sm text-amber-800 mt-1" data-testid="dup-active-opp-warning">This person already has an active recruiting record ({result.active_opportunity?.human_id}). A duplicate won't be created.</p>}
        </div>
      </div>

      {!crossOrg && result.name && (
        <div className="mt-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid="dup-person-card">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-900">{result.name}</span>
            <span className="text-xs text-slate-400">{result.human_id}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-600">
            {result.organization_name && <span>District: <strong>{result.organization_name}</strong></span>}
            <span>Status: <strong className="capitalize">{result.person_status}</strong></span>
            <span>Recruiter: <strong>{result.assigned_recruiter_name || "Unassigned"}</strong></span>
            <span data-testid="dup-classifications">Current classifications: <strong className="capitalize">{(result.classifications || []).map((c) => c.replace(/_/g, " ")).join(", ") || "None"}</strong></span>
            {(result.email || result.mobile) && <span>{result.email}{result.mobile ? ` · ${result.mobile}` : ""}</span>}
            {result.has_active_opportunity && <span className="text-emerald-700">Active opportunity: <strong>{result.active_opportunity?.human_id}</strong></span>}
          </div>
          {result.scope_class === "same_org_assigned" && !actions.includes("reassign") && (
            <p className="mt-2 text-xs text-amber-700" data-testid="dup-assigned-note">Already assigned to {result.assigned_recruiter_name || "another recruiter"}. Contact a manager to request reassignment.</p>
          )}
        </div>
      )}

      {crossOrg && (
        <div className="mt-3" data-testid="dup-transfer">
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Reason for transfer / access request</label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dup-transfer-reason"
            placeholder="Briefly explain why you need access to this person." />
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 mt-4">
        {actions.filter((a) => a !== "cancel").map(btn)}
        <Button variant="ghost" onClick={onReset} data-testid="dup-action-add-another">Back to form</Button>
      </div>
    </div>
  );
}
